<script setup lang="ts">
import type { ButtonVariants } from '.';
import { cn } from '@elveris/ui/lib/utils'
import { Primitive } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { computed } from 'vue'
import { buttonVariants } from '.'

interface Props {
  variant?: ButtonVariants['variant']
  size?: ButtonVariants['size']
  loading?: boolean
  disabled?: boolean
  class?: HTMLAttributes['class']
  /** Classes for the inner slot wrapper, useful for stacked or full-width content. */
  contentClass?: HTMLAttributes['class']
  type?: 'button' | 'submit' | 'reset'
  /**
   * Render the single child element instead of a <button>, merging the button
   * styling onto it.
   *
   * S-BUTTON-AS-CHILD — seven call sites already write `<Button as-child>`
   * around a `<RouterLink>` and have been getting nothing: the prop did not
   * exist, so `as-child` fell through as a plain HTML attribute onto the
   * <button>, the link rendered inside it unstyled, and the button's own
   * classes never reached the thing the user actually clicks.
   *
   * Measured on the 404 page: both recovery links rendered at 87x20 and 70x20
   * with `class="inline-flex items-center gap-2"` and nothing else — no
   * padding, no background, under WCAG 2.2 SC 2.5.8's 24x24 floor, and not
   * looking like buttons at all on the one page whose whole job is to get a
   * lost user back.
   *
   * The loading spinner is skipped in this mode: `as-child` requires exactly
   * one child, and a caller passing a link is not passing `loading`.
   */
  asChild?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
})

const emit = defineEmits<{
  /**
   * `Event`, NOT `MouseEvent` — deliberately.
   *
   * `handleKeydown` also emits `click` when the user presses Enter or Space
   * (keyboard activation for accessibility), so the payload is a `KeyboardEvent` on
   * that path. A consumer handler typed `(event: MouseEvent)` is therefore unsound:
   * it type-errors here, and reading `clientX` / `shiftKey` off a keyboard activation
   * yields `undefined`. Type consumer handlers as `Event` and narrow with
   * `instanceof MouseEvent` if coordinates are needed.
   */
  click: [event: Event]
}>()

const contentClasses = computed(() => cn(
  'flex min-w-0 max-w-full items-center gap-2',
  props.contentClass,
))

const buttonClasses = computed(() => cn(
  buttonVariants({ variant: props.variant, size: props.size }),
  // Micro-interaction: Haptic active state. Sprint S-MOTION-TOKENS-1
  // (2026-06-04) — Tailwind `duration-200 ease-out` hardcode pulled
  // out into `theme-base.css` motion-token CSS vars so per-tenant
  // motion language (snappy fintech vs. calm enterprise) is one
  // override away. The arbitrary-value class threads the var through.
  // S-NO-SCALE-GESTURE (2026-09-06) — `active:scale-[0.97]` KALDIRILDI.
  // Basma geri bildirimi ÖLÇÜ DEĞİŞTİRMEMELİ; yerine 1px'lik dikey kayma
  // geldi. Gerekçe için bkz. elveris-avm docs/DESIGN_REVIEW_TODO.md Tur 21.
  // `translate` transform olduğu için layout'u yeniden akıtmaz ve düğmenin
  // genişliği/yüksekliği sabit kalır — ölçek küçültmesi ise bir toolbar
  // satırında komşularla hizayı anlık bozuyordu.
  'active:translate-y-px transition-theme',
  // Positioning for loading spinner
  'relative inline-flex',
  props.loading && 'opacity-80 pointer-events-none cursor-wait',
  props.disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
  props.class
))

function handleClick(event: Event) {
  if (props.disabled || props.loading) {
    // `asChild` may render an anchor, which has no native disabled state. Keep
    // the visual disabled state from also navigating or reaching parent actions.
    event.preventDefault()
    event.stopPropagation()
    return
  }

  emit('click', event)
}

function handleKeydown(event: KeyboardEvent) {
  // Support keyboard activation for accessibility (Enter and Space keys)
  if ((event.key === 'Enter' || event.key === ' ') && !props.disabled && !props.loading) {
    event.preventDefault()
    emit('click', event)
  }
}

/*
  `as-child` dalı: stilizasyonu çağıranın kendi elemanına (RouterLink, <a>)
  devreder, böylece tıklanan şey DÜĞMENİN KENDİSİ olur. Ayrıntı için
  `Props.asChild` notuna bakın.

  NOT (2026-09-11): bu açıklama `<template>`'in İLK düğümü olarak HTML
  yorumu halindeydi. Yorum + `v-if`/`v-else` çifti şablona iki kök düğüm
  verdiği için Vue bileşeni Fragment'a sarıyordu; fragment kökünde
  `inheritAttrs` çalışmadığından `<Button id=... @click=...>` gibi tüm
  fallthrough öznitelikleri sessizce düşüyor, `wrapper.classes()` boş
  dönüyordu. Button.test.ts'teki 46 hatanın tamamı buydu — açıklama script
  bloğuna taşındığında 70/70 yeşile döndü. Şablon tek kök düğümde kalmalı.
*/
</script>

<template>
  <Primitive
    v-if="props.asChild"
    as-child
    :class="buttonClasses"
    :aria-disabled="props.disabled || props.loading ? 'true' : undefined"
    @click="handleClick"
  >
    <slot />
  </Primitive>
  <button
    v-else
    :type="props.type"
    :class="buttonClasses"
    :disabled="props.disabled || props.loading"
    @click="handleClick"
    @keydown="handleKeydown"
  >
    <div v-if="props.loading" class="absolute inset-0 flex items-center justify-center">
      <svg class="h-4 w-4 animate-spin opacity-60" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>
    <!-- `min-w-0 max-w-full`: the wrapper may shrink to the button. Without them it was
         as wide as its content, so a consumer's `truncate` / `min-w-0 flex-1` inside the
         slot never engaged and a long label overflowed BOTH sides of a centred button
         (the shell's organisation switcher: 376px of content in a 250px button). -->
    <div :class="props.loading ? 'invisible' : contentClasses">
      <slot />
    </div>
  </button>
</template>
